import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router';
import { z } from 'zod';

import { apiErrorMessage } from '../../shared/api/errors';
import { Button } from '../../shared/ui/Button';
import { TextField } from '../../shared/ui/TextField';
import { forgotPassword } from './api';
import { AuthShell } from './AuthShell';

const emailSchema = z.string().email('Введите корректный e-mail');

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState('');

  const mutation = useMutation({ mutationFn: forgotPassword });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = emailSchema.safeParse(email.trim());
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Введите корректный e-mail');
      return;
    }
    setFieldError('');
    mutation.mutate(parsed.data);
  };

  return (
    <AuthShell subtitle="Восстановление пароля">
      {mutation.isSuccess ? (
        <div className="space-y-4 text-sm text-ink">
          <p>
            Если адрес <span className="font-medium">{email.trim()}</span> зарегистрирован, на него
            придёт письмо со ссылкой для смены пароля.
          </p>
          <p className="text-muted">
            Ссылка действует ограниченное время. Не пришло письмо — проверьте папку «Спам» или
            обратитесь к администратору.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-muted">
            Укажите e-mail, с которым входите в систему, — пришлём ссылку для смены пароля.
          </p>
          <TextField
            label="E-mail"
            type="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={fieldError}
          />
          {mutation.isError && (
            <p className="text-sm text-danger">
              {apiErrorMessage(mutation.error, 'Не удалось отправить запрос')}
            </p>
          )}
          <Button type="submit" disabled={mutation.isPending} className="w-full">
            {mutation.isPending ? 'Отправляем…' : 'Отправить ссылку'}
          </Button>
        </form>
      )}
      <p className="mt-4 text-center text-sm">
        <Link to="/login" className="font-medium text-accent hover:text-accent-600">
          ← Ко входу
        </Link>
      </p>
    </AuthShell>
  );
}
